// src/utils/liveProgress.ts
//
// Průběh živého závodu – sdílený tabulí a obrazovkou rozhodčího (fáze 4a,
// A4; dřív tabule zaokrouhlovala nahoru a ukázala 100 % i s chybějícím
// výsledkem, rozhodčí dolů) – a přehled chybějících výsledků před
// ukončením závodu (F2, otázka 9).

import type { LiveResult } from "../types/liveScoring";

/** Výsledek stanoviště existuje (zapsaný, i nula). */
const hasStationEntry = (result: LiveResult | undefined, stationId: string): boolean => {
  const stations = result ? result.stations : undefined;
  return !!stations && stations[stationId] != null;
};

export interface LiveProgress {
  /** Uložené výsledky stanovišť aktivních střelců. */
  done: number;
  /** Aktivní střelci × nastavená stanoviště. */
  expected: number;
  /** Procenta zaokrouhlená DOLŮ (100 jen když je hotovo vše). */
  pct: number;
}

export function liveProgress(
  activeStartNumbers: Iterable<number>,
  stationIds: ReadonlyArray<string>,
  results: { [startNumber: number]: LiveResult | undefined }
): LiveProgress {
  const active = Array.from(new Set(Array.from(activeStartNumbers)));
  let done = 0;
  active.forEach((sn) => {
    stationIds.forEach((id) => {
      if (hasStationEntry(results[sn], id)) done += 1;
    });
  });
  const expected = active.length * stationIds.length;
  const pct = expected > 0 ? Math.min(100, Math.floor((done / expected) * 100)) : 0;
  return { done, expected, pct };
}

export interface MissingResults {
  /** Počet chybějících výsledků (stanoviště × střelec). */
  count: number;
  /** Po stanovištích (v pořadí stanovišť), jen ta, kde něco chybí. */
  byStation: Array<{ stationId: string; number: number; name: string; startNumbers: number[] }>;
}

/** Chybějící výsledky aktivních střelců na nastavených stanovištích. */
export function missingResults(
  activeStartNumbers: Iterable<number>,
  stations: ReadonlyArray<{ id: string; number?: unknown; name?: unknown }>,
  results: { [startNumber: number]: LiveResult | undefined }
): MissingResults {
  const active = Array.from(new Set(Array.from(activeStartNumbers))).sort((a, b) => a - b);
  const byStation: MissingResults["byStation"] = [];
  let count = 0;
  stations.forEach((st) => {
    const missing = active.filter((sn) => !hasStationEntry(results[sn], st.id));
    if (missing.length === 0) return;
    count += missing.length;
    byStation.push({
      stationId: st.id,
      number: typeof st.number === "number" ? st.number : Number(st.number) || 0,
      name: typeof st.name === "string" ? st.name : "",
      startNumbers: missing,
    });
  });
  return { count, byStation };
}

/** Kolik čísel na stanoviště a kolik stanovišť přehled vypíše (zbytek „…"). */
const MAX_NUMBERS = 12;
const MAX_STATIONS = 6;

/**
 * Text přehledu pro dialog ukončení: „Chybí 3 výsledky – stanoviště 2: #12,
 * #15; stanoviště 3: #12." s radou počkat na odeslání z telefonů; null =
 * nic nechybí.
 */
export function missingResultsText(missing: MissingResults): string | null {
  if (missing.count === 0) return null;
  const parts = missing.byStation.slice(0, MAX_STATIONS).map((st) => {
    const numbers = st.startNumbers.slice(0, MAX_NUMBERS).map((sn) => `#${sn}`).join(", ");
    const more = st.startNumbers.length > MAX_NUMBERS ? ` a dalších ${st.startNumbers.length - MAX_NUMBERS}` : "";
    return `stanoviště ${st.number}: ${numbers}${more}`;
  });
  if (missing.byStation.length > MAX_STATIONS) parts.push("…");
  const noun = missing.count === 1 ? "výsledek" : missing.count < 5 ? "výsledky" : "výsledků";
  return (
    `Chybí ${missing.count} ${noun} – ${parts.join("; ")}. ` +
    "Pokud je mají rozhodčí neodeslané v telefonu, počkejte na jejich odeslání."
  );
}
